import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";

export type ReporterAiMode = "inline" | "strip" | "off";

/**
 * Fraction of the report column's vertical space allocated to the
 * SuggestionStrip in strip mode (0..1). 0.5 = 50/50 split. Persisted
 * per-device via localStorage so the rad's preferred ratio survives
 * reloads. Bounded by STRIP_HEIGHT_MIN/MAX on apply.
 */
export const stripHeightFractionAtom = atomWithStorage<number>("stripHeightFraction", 0.5);
export const STRIP_HEIGHT_MIN = 0.15;
export const STRIP_HEIGHT_MAX = 0.85;

/**
 * Reporter A/B mode. Authoritative copy lives in `a2z-medi-flow`
 * (PK=USER#<email>, SK=PREFS); `useUserPrefs` hydrates this atom on mount
 * and writes back through it. Components that don't need the mutation can
 * read this atom directly.
 *
 * Default `"inline"` matches the production behavior for users with no
 * PREFS row — flipping the toggle is what creates the row.
 */
export const reporterAiModeAtom = atom<ReporterAiMode>("inline");

/**
 * Whether the editor surfaces review-tier ("maybe" / to-consider) AI
 * findings — the ones that fired only one operating-point notch looser than
 * their default threshold. Default-rung findings are unaffected. "Maybe" is
 * an internal presentation tier, not a probability or calibrated confidence.
 *
 * Default `false`: review-tier findings are hidden until the rad opts in
 * via the report settings gear. Persisted per-device via localStorage (a
 * display preference, like `stripHeightFractionAtom`), not the server-side
 * PREFS row.
 *
 * `getOnInit: true` so the first render already carries the stored value
 * instead of flashing the `false` default — the inline reset effect keys on
 * changes to this atom, and a default→stored transition on mount would pop a
 * phantom "reset draft?" prompt for users who had it on.
 */
export const showLowConfidenceFindingsAtom = atomWithStorage<boolean>(
  "showLowConfidenceFindings",
  false,
  undefined,
  { getOnInit: true },
);

/**
 * Whether the editor shows normal/negative fields ("no normals" hides them).
 * Persisted per-device via localStorage so the rad's view preference carries
 * across cases — in the reader study this lets a reader set their view during
 * the untimed acclimation case and have it hold through the timed block,
 * instead of resetting to shown on every case mount. Default `true` (normals
 * shown). "Reset to AI draft" still flips it back to shown.
 */
export const showNormalFieldsAtom = atomWithStorage<boolean>("showNormalFields", true, undefined, {
  getOnInit: true,
});

/**
 * Focused SuggestionStrip card — the row that ` / ⌃` should target.
 *
 * Set by SuggestionStrip when the rad clicks a card; cleared on outside
 * click or accept/dismiss. PrototypeReport's existing backtick handler
 * checks this atom FIRST so the gesture works identically to inline mode
 * (cursor-in-chip-with-text → refine via voiceAdjust). When this atom is
 * non-null, ` is push-to-talk voice-adjust on the focused card; the
 * spoken instruction goes through the same gpt-4o-mini pipeline as
 * editor chips.
 *
 * `apply` writes the LLM-tuned text back to the strip's local override.
 */
export interface FocusedStripRow {
  id: string;
  originalText: string;
  apply: (next: string) => void;
}

export const focusedStripRowAtom = atom<FocusedStripRow | null>(null);
